import React from 'react';
import PropTypes from 'prop-types';
import ReactResizeDetector from 'react-resize-detector';
import JumperMapWidget from './MapWidget';
import JumperMapConnector from './MapConnector';
import Minimap from './Minimap';
import { WIDGETS } from '../../constants';
import { objToArr } from '../../helpers';
import Styles from './Map.styles';
import MapImage from './MapImage';

class Map extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      display: 'none',
      imageScale: 0.01,
      imagePositionX: 0.5,
      imagePositionY: 0.5,
      widgets: props.widgets,
      connectors: props.connectors,
      selectedWidget: null,
      newConnector: null,
      imageLoaded: false,
      zoom: 0,
      maxZoom: 0,
      initialScale: null,
    };
    this.imageOnLoad = this.imageOnLoad.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.widgetOnSelect = this.widgetOnSelect.bind(this);
    this.widgetOnChangePosition = this.widgetOnChangePosition.bind(this);
    this.widgetOnStartConnect = this.widgetOnStartConnect.bind(this);
    this.connectorOnMouseMove = this.connectorOnMouseMove.bind(this);
    this.connectorOnMouseUp = this.connectorOnMouseUp.bind(this);
    this.widgetOnMouseDrop = this.widgetOnMouseDrop.bind(this);
    this.widgetOnMouseUp = this.widgetOnMouseUp.bind(this);
    this.onDropConnector = this.onDropConnector.bind(this);
    this.onZoomIn = this.onZoomIn.bind(this);
    this.onZoomOut = this.onZoomOut.bind(this);

    this.imageWidth = 0;
    this.imageHeight = 0;
    this.imagePositionX = 0.5;
    this.imagePositionY = 0.5;
    this.imageScale = 0.01;
    this.editorWidth = 0;
    this.editorHeight = 0;
    this.grabX = 0;
    this.grabY = 0;
    this.grabLeft = 0;
    this.grabTop = 0;
    this.zoom = 1;

    this.newWidgetId = 0;
    this.newConnectorId = 0;
  }

  componentWillMount() {
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillReceiveProps(newProps) {
    const { widgets, connectors } = this.state;
    const { widgets: newWidgets, connectors: newConnectors } = newProps;
    this.setState({
      widgets: { ...widgets, ...newWidgets },
      connectors: { ...connectors, ...newConnectors },
    });
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  onResize() {
    if (this.editor) {
      this.editorWidth = this.editor.offsetWidth;
      this.editorHeight = this.editor.offsetHeight;
      let maxZoom = 0;
      let scale = this.getImageScale();
      let { initialScale } = this.state;
      if (initialScale === null) {
        initialScale = scale;
        this.initialScale = initialScale;
        this.imageScale = initialScale;
      }

      const {
        imagePositionX,
        imagePositionY,
        imageScale,
        imageHeightWithEmptySpace,
        imageWidthWithEmptySpace,
      } = this.fitMap();

      while (scale * 1.1 < 10) {
        scale *= 1.1;
        maxZoom += 1;
      }

      const zoom = this.state.zoom + 1;
      this.recalculate(zoom, {
        imagePositionX,
        imagePositionY,
        imageScale,
        imageHeightWithEmptySpace,
        imageWidthWithEmptySpace,
        maxZoom,
      });
    }
  }

  onMouseDown(event) {
    if (event.nativeEvent.target === this.container) {
      event.nativeEvent.preventDefault();
      this.container.addEventListener('mousemove', this.onMouseMove);
      this.container.addEventListener('mouseup', this.onMouseUp);

      this.grabX = event.nativeEvent.screenX;
      this.grabY = event.nativeEvent.screenY;
      this.grabLeft = event.nativeEvent.target.offsetLeft;
      this.grabTop = event.nativeEvent.target.offsetTop;
    }
  }

  onMouseMove(event) {
    event.preventDefault();
    this.imagePositionX = (this.grabLeft + (event.screenX - this.grabX)) / this.editorWidth;
    this.imagePositionY = (this.grabTop + (event.screenY - this.grabY)) / this.editorHeight;
    this.recalculate();
  }

  onMouseUp(event) {
    event.preventDefault();
    this.container.removeEventListener('mousemove', this.onMouseMove);
    this.container.removeEventListener('mouseup', this.onMouseUp);
  }

  onWheel(event) {
    let scale = this.imageScale - Math.sign(event.nativeEvent.deltaY) * this.imageScale * 0.1;
    const zoom = Math.max(
      0,
      Math.min(this.state.zoom - Math.sign(event.nativeEvent.deltaY) * 1, this.state.maxZoom),
    );
    if (scale < 10) {
      scale = Math.min(scale, 10);
      const percentage = scale / this.imageScale;
      this.imageScale = scale;

      const originX = event.nativeEvent.clientX;
      const originY = event.nativeEvent.clientY;
      const box = this.container.getBoundingClientRect();
      const posX = box.left + box.width * 0.5;
      const posY = box.top + box.height * 0.5;

      const offsetX = (posX - originX) * percentage - (posX - originX);
      const offsetY = (posY - originY) * percentage - (posY - originY);

      this.imagePositionX = (this.container.offsetLeft + offsetX) / this.editorWidth;
      this.imagePositionY = (this.container.offsetTop + offsetY) / this.editorHeight;
      this.recalculate(zoom);
    }
  }

  onDropConnector() {
    if (this.state.newConnector) {
      this.setState({
        newConnector: null,
      });
    }
  }

  onZoomIn() {
    const scale = Math.min(this.imageScale + this.imageScale * 0.1, 10);
    const zoom = Math.max(0, Math.min(this.state.zoom + 1, this.state.maxZoom));
    const percentage = scale / this.imageScale;
    this.imageScale = scale;

    const originX = this.editorWidth / 2;
    const originY = this.editorHeight / 2;
    const box = this.container.getBoundingClientRect();
    const posX = box.left + box.width * 0.5;
    const posY = box.top + box.height * 0.5;

    const offsetX = (posX - originX) * percentage - (posX - originX);
    const offsetY = (posY - originY) * percentage - (posY - originY);

    this.imagePositionX = (this.container.offsetLeft + offsetX) / this.editorWidth;
    this.imagePositionY = (this.container.offsetTop + offsetY) / this.editorHeight;
    this.recalculate(zoom);
  }

  onZoomOut() {
    const scale = Math.min(this.imageScale - this.imageScale * 0.1, 10);
    const percentage = scale / this.imageScale;
    this.imageScale = scale;
    const zoom = Math.max(0, Math.min(this.state.zoom - 1, this.state.maxZoom));
    const originX = this.editorWidth / 2;
    const originY = this.editorHeight / 2;
    const box = this.container.getBoundingClientRect();
    const posX = box.left + box.width * 0.5;
    const posY = box.top + box.height * 0.5;

    const offsetX = (posX - originX) * percentage - (posX - originX);
    const offsetY = (posY - originY) * percentage - (posY - originY);

    this.imagePositionX = (this.container.offsetLeft + offsetX) / this.editorWidth;
    this.imagePositionY = (this.container.offsetTop + offsetY) / this.editorHeight;

    this.recalculate(zoom);
  }

  onKeyDown(event) {
    if (event.keyCode === 8) {
      if (Object.keys(this.props.selectedWidget).length > 0) {
        this.props.removeWidget(this.props.selectedWidget);
      }
    }
  }

  getImageScale(imageScale = 0.1) {
    let newImageScale = imageScale;
    if (this.imageWidth / this.imageHeight < this.editorWidth / this.editorHeight) {
      if (this.imageHeight * newImageScale < this.editorHeight) {
        newImageScale = this.editorHeight / this.imageHeight;
      }
    } else if (this.imageWidth * newImageScale < this.editorWidth) {
      newImageScale = this.editorWidth / this.imageWidth;
    }

    return newImageScale;
  }

  recalculate(zoom = null, state) {
    const {
      imagePositionX,
      imagePositionY,
      imageScale,
      imageHeightWithEmptySpace,
      imageWidthWithEmptySpace,
    } = this.fitMap();

    this.setState({
      imagePositionX,
      imagePositionY,
      imageScale,
      minimapHeightPercentage: this.editorHeight / imageHeightWithEmptySpace,
      minimapWidthPercentage: this.editorWidth / imageWidthWithEmptySpace,
      positionXPercentage: this.editorWidth * imagePositionX / imageWidthWithEmptySpace * -1 + 0.5,
      positionYPercentage:
        this.editorHeight * imagePositionY / imageHeightWithEmptySpace * -1 + 0.5,
      zoom: zoom || this.state.zoom,
      ...state,
    });
  }

  widgetOnMouseDrop(widget) {
    if (this.state.newConnector) {
      this.state.newConnector.endWidget = { ...widget, id: null };
      if (
        this.state.newConnector.startWidget.key !== this.state.newConnector.endWidget.key &&
        !this.isConnectorExist(
          this.state.newConnector.startWidget,
          this.state.newConnector.endWidget,
        )
      ) {
        // const connector = this.state.newConnector;
        const { projectId } = this.props.match.params;
        this.props.addConnector(this.state.newConnector, projectId);
      }
    }
  }

  widgetOnMouseUp(widget) {
    const { widgets, connectors } = this.state;
    const newWidgets = Object.keys(widgets).reduce((obj, key) => {
      const tmp = obj;
      tmp[key] = widgets[key];
      if (widgets[key].sceneId === widget.sceneId) {
        tmp[key] = widget;
      }
      return tmp;
    }, {});
    const newConnectors = Object.keys(connectors).reduce((obj, key) => {
      const newConnector = connectors[key];
      const tmpObj = obj;
      if (newConnector.startWidget.sceneId === widget.sceneId) {
        newConnector.startWidget = { ...widget, id: null };
      }

      if (newConnector.endWidget.sceneId === widget.sceneId) {
        newConnector.endWidget = { ...widget, id: null };
      }
      tmpObj[key] = newConnector;
      return tmpObj;
    }, {});
    const { projectId } = this.props.match.params;
    this.props.updateMap(newWidgets, newConnectors, projectId);
  }

  isConnectorExist(startWidget, endWidget) {
    for (let i = 0; i < this.state.connectors.length; i++) {
      if (
        (startWidget.key === this.state.connectors[i].startWidget.key &&
          endWidget.key === this.state.connectors[i].endWidget.key) ||
        (startWidget.key === this.state.connectors[i].endWidget.key &&
          endWidget.key === this.state.connectors[i].startWidget.key)
      ) {
        return true;
      }
    }
    return false;
  }

  widgetOnStartConnect(widget) {
    window.addEventListener('mousemove', this.connectorOnMouseMove);
    window.addEventListener('mouseup', this.connectorOnMouseUp);

    const connector = {
      startWidget: { ...widget, id: null },
      startPositionX: widget.positionX,
      startPositionY: widget.positionY,
      endPositionX: widget.positionX,
      endPositionY: widget.positionY,
    };
    this.setState({
      newConnector: connector,
    });
  }

  widgetOnChangePosition(widget, event) {
    const { widgets, connectors } = this.state;
    const newWidgets = Object.keys(widgets).reduce((obj, key) => {
      const tmp = obj;
      tmp[key] = widgets[key];
      if (widgets[key].sceneId === widget.sceneId) {
        tmp[key] = widget;
      }
      return tmp;
    }, {});
    const newConnectors = Object.keys(connectors).reduce((obj, key) => {
      const newConnector = connectors[key];
      const tmpObj = obj;
      if (newConnector.startWidget.sceneId === widget.sceneId) {
        newConnector.startWidget = { ...widget, id: null };
      }

      if (newConnector.endWidget.sceneId === widget.sceneId) {
        newConnector.endWidget = { ...widget, id: null };
      }
      tmpObj[key] = newConnector;
      return tmpObj;
    }, {});
    this.setState({
      widgets: newWidgets,
      connectors: newConnectors,
    });
  }

  widgetOnSelect(widget) {
    this.props.selectWidget(widget);
  }

  addPortal(item, position) {
    const widgets = this.props.widgets;
    const foundWidget = Object.entries(widgets).find(([key, widget]) => widget.sceneId === item.id);
    if (!foundWidget) {
      const isMain = Object.keys(this.props.widgets).length === 0;
      const box = this.container.getBoundingClientRect();
      let positionX = (position.x - box.x) / box.width;
      let positionY = (position.y - box.y) / box.height;
      if (positionX < 0 || positionX > 1 || positionY < 0 || positionY > 1) {
        positionX = Math.min(Math.max(positionX, 0), 1);
        positionY = Math.min(Math.max(positionY, 0), 1);
        positionX = 0.5;
        positionY = 0.5;
      }
      const widget = {
        positionX,
        positionY,
        positionZ: 165,
        type: WIDGETS.PORTAL,
        sceneId: item.id,
        isMain,
      };
      const { projectId } = this.props.match.params;
      this.props.addPortal(widget, projectId);
    }
  }

  fitMap() {
    this.imageScale = this.getImageScale(this.imageScale);
    const imageWidthWithEmptySpace =
      this.imageWidth * this.imageScale +
      Math.max(0, (this.editorWidth - this.imageWidth * this.initialScale) * this.imageScale);
    const imageHeightWithEmptySpace =
      this.imageHeight * this.imageScale +
      Math.max(0, (this.editorHeight - this.imageHeight * this.initialScale) * this.imageScale);

    this.imagePositionX = Math.min(
      Math.max(
        this.imagePositionX,
        0.5 - (imageWidthWithEmptySpace - this.editorWidth) / this.editorWidth * 0.5,
      ),
      0.5 + (imageWidthWithEmptySpace - this.editorWidth) / this.editorWidth * 0.5,
    );
    this.imagePositionX = imageWidthWithEmptySpace < this.editorWidth ? 0.5 : this.imagePositionX;
    this.imagePositionY = Math.min(
      Math.max(
        this.imagePositionY,
        0.5 - (imageHeightWithEmptySpace - this.editorHeight) / this.editorHeight * 0.5,
      ),
      0.5 + (imageHeightWithEmptySpace - this.editorHeight) / this.editorHeight * 0.5,
    );
    this.imagePositionY = imageHeightWithEmptySpace < this.editorHeight ? 0.5 : this.imagePositionY;

    return {
      imagePositionX: this.imagePositionX,
      imagePositionY: this.imagePositionY,
      imageScale: this.imageScale,
      imageWidthWithEmptySpace,
      imageHeightWithEmptySpace,
    };
  }

  imageOnLoad(event) {
    this.imageWidth = event.target.width;
    this.imageHeight = event.target.height;
    this.setState({
      display: 'block',
      imageLoaded: true,
    });
    this.recalculate();
  }

  connectorOnMouseMove(event) {
    event.preventDefault();
    const mouseX = event.clientX;
    const mouseY = event.clientY;
    const box = this.container.getBoundingClientRect();
    const endPositionX = (mouseX - box.left) / box.width;
    const endPositionY = (mouseY - box.top) / box.height;
    this.state.newConnector.endPositionX = endPositionX;
    this.state.newConnector.endPositionY = endPositionY;
    this.setState({
      newConnector: this.state.newConnector,
    });
  }

  connectorOnMouseUp(event) {
    event.preventDefault();
    window.removeEventListener('mousemove', this.connectorOnMouseMove);
    window.removeEventListener('mouseup', this.connectorOnMouseUp);
  }

  prepareItems(items, icon = null) {
    const output = objToArr(Object.assign({}, items)).map((item) => {
      const tmp = item;
      tmp.id = tmp.key;
      tmp.text = tmp.name || '';
      if (icon) {
        tmp.icon = icon;
      }
      return tmp;
    });

    return output;
  }

  renderMinimap() {
    return (
      <Minimap
        editorWidth={this.editorWidth}
        editorHeight={this.editorHeight}
        imageScale={this.state.imageScale}
        imageWidth={this.imageWidth}
        imageHeight={this.imageHeight}
        minimapHeightPercentage={this.state.minimapHeightPercentage}
        minimapWidthPercentage={this.state.minimapWidthPercentage}
        positionXPercentage={this.state.positionXPercentage}
        positionYPercentage={this.state.positionYPercentage}
        onZoomIn={this.onZoomIn}
        onZoomOut={this.onZoomOut}
        zoom={this.state.zoom}
        maxZoom={this.state.maxZoom}
      />
    );
  }

  renderConnector(connector) {
    return <JumperMapConnector {...connector} containerRef={this.container} selected />;
  }

  renderWidget(id, widget) {
    return (
      <JumperMapWidget
        key={id}
        id={id}
        object={widget}
        selected={this.props.selectedWidget ? this.props.selectedWidget === id : null}
        onSelect={this.widgetOnSelect}
        onChangePosition={this.widgetOnChangePosition}
        onStartConnect={this.widgetOnStartConnect}
        onMouseDrop={this.widgetOnMouseDrop}
        onMouseUp={this.widgetOnMouseUp}
        {...widget}
      >
        {widget.type === 'portal' ? '' : ''}
      </JumperMapWidget>
    );
  }

  render() {
    const { connectDropTarget, mapImageUrl } = this.props;
    const imageWidth = Math.round(this.imageWidth * this.state.imageScale);
    const imageHeight = Math.round(this.imageHeight * this.state.imageScale);

    return connectDropTarget(
      <div
        role="presentation"
        tabIndex="-1"
        className="JumperMapEditor"
        style={Styles.divStyle(this.state.display)}
        ref={(editor) => {
          this.editor = editor;
        }}
        onWheel={this.onWheel}
        onMouseUp={this.onDropConnector}
      >
        {this.state.imageLoaded && this.renderMinimap()}
        <div
          role="presentation"
          className="Container"
          ref={(container) => {
            this.container = container;
          }}
          style={Styles.containerStyle(
            imageWidth,
            imageHeight,
            this.state.imagePositionX,
            this.state.imagePositionY,
          )}
          onMouseDown={this.onMouseDown}
        >
          <MapImage
            imageUrl={mapImageUrl}
            onLoad={this.imageOnLoad}
            unselectable="on"
            alt=""
            imageWidth={imageWidth}
            imageHeight={imageHeight}
          >
            {this.state.newConnector && this.renderConnector(this.state.newConnector)}
            {this.state.connectors &&
              Object.entries(this.state.connectors).map(([id, connector]) =>
                this.renderConnector(connector, id),
              )}
            {this.state.widgets &&
              Object.entries(this.state.widgets).map(([id, widget]) =>
                this.renderWidget(id, widget),
              )}
          </MapImage>
        </div>
        <ReactResizeDetector handleWidth handleHeight onResize={this.onResize} skipOnMount />
      </div>,
    );
  }
}
Map.propTypes = {
  connectDropTarget: PropTypes.func.isRequired,
  mapImageUrl: PropTypes.string.isRequired,
};

export default Map;



// WEBPACK FOOTER //
// ./src/components/Map/Map.js